"use client"

import { FC, useRef } from "react"
import { Content, asLink } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  type Variants,
  useScroll,
  useTransform,
  useSpring,
} from "motion/react"
import { vFadeUp, vFadeIn, vStaggerContainer } from "@/lib/animations"
import { DecorativeLine } from "@/components/ui/decorative-line"
import { Button } from "@/components/ui/button"

export type SocialMediaHighlightProps =
  SliceComponentProps<Content.SocialMediaHighlightSlice>

const vCardStagger: Variants = {
  hidden: { opacity: 0, y: 32, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
}

const SocialMediaHighlight: FC<SocialMediaHighlightProps> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  })

  // Parallax: glow blob drifts up as you scroll through the section
  const glowY = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"])
  const smoothGlowY = useSpring(glowY, { stiffness: 60, damping: 20 })

  // Card alternating parallax: even cards rise, odd cards descend
  const cardEvenY = useTransform(scrollYProgress, [0, 1], [40, -70])
  const cardOddY = useTransform(scrollYProgress, [0, 1], [-40, 70])
  const smoothEvenY = useSpring(cardEvenY, { stiffness: 50, damping: 18 })
  const smoothOddY = useSpring(cardOddY, { stiffness: 50, damping: 18 })

  // Intro text subtle upward drift as you scroll deeper into the section
  const introY = useTransform(scrollYProgress, [0, 0.7], [0, -48])
  const smoothIntroY = useSpring(introY, { stiffness: 60, damping: 22 })

  // Vertical progress line grows downward as section scrolls into view
  const progressScaleY = useTransform(scrollYProgress, [0.05, 0.85], [0, 1])

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        ref={sectionRef}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden section-py"
        style={{
          background:
            "radial-gradient(ellipse 100% 80% at 50% 0%, var(--sacred-cream) 0%, white 65%)",
        }}
      >
        {/* Vertical scroll progress line — grows down the left edge */}
        <motion.div
          aria-hidden
          className="absolute inset-y-0 left-4 w-0.5 origin-top rounded-full"
          style={{
            scaleY: progressScaleY,
            background:
              "linear-gradient(to bottom, var(--sacred-orange), var(--sacred-gold), transparent)",
          }}
        />

        {/* Soft ambient glow top-center — parallax */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 size-120 -translate-x-1/2 rounded-full opacity-30 blur-3xl"
          style={{ background: "var(--sacred-gold)", y: smoothGlowY }}
        />

        <m.div
          className="relative container flex flex-col items-center gap-10 sm:gap-12 md:gap-14"
          variants={vStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {/* Intro */}
          <m.div
            variants={vFadeUp}
            className="flex flex-col items-center gap-4 text-center"
            style={{ y: smoothIntroY }}
          >
            {primary.heading && (
              <PrismicRichText
                field={primary.heading}
                components={{
                  heading2: ({ children }) => <h2>{children}</h2>,
                }}
              />
            )}
            <DecorativeLine className="w-24" />
            {primary.description && (
              <div className="max-w-xl">
                <PrismicRichText
                  field={primary.description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-muted-foreground">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </m.div>

          {/* Social cards */}
          {primary.socials && primary.socials.length > 0 && (
            <m.div
              variants={vStaggerContainer}
              className="grid w-full max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2"
            >
              {primary.socials.map((social, i) => {
                const href = asLink(social.cta)
                const ctaText =
                  social.cta && "text" in social.cta && social.cta.text
                    ? String(social.cta.text)
                    : "Follow"
                return (
                  <m.div
                    key={i}
                    variants={vCardStagger}
                    className="group relative flex flex-col items-center gap-5 overflow-hidden rounded-3xl p-7 text-center"
                    style={{
                      background:
                        "linear-gradient(145deg, var(--social-card-bg-from) 0%, var(--social-card-bg-to) 100%)",
                      boxShadow:
                        "0 4px 24px -4px var(--social-card-shadow), inset 0 1px 0 oklch(1 0 0 / 0.07)",
                      y: i % 2 === 0 ? smoothEvenY : smoothOddY,
                    }}
                  >
                    {/* Hover glow bloom */}
                    <div
                      className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      style={{
                        background:
                          "radial-gradient(ellipse 80% 60% at 50% 0%, var(--social-card-hover-glow) 0%, transparent 70%)",
                      }}
                    />

                    {/* Golden top accent line */}
                    <div
                      className="absolute inset-x-8 top-0 h-px rounded-full transition-all duration-500 group-hover:inset-x-4"
                      style={{
                        background:
                          "linear-gradient(to right, transparent, var(--sacred-gold), transparent)",
                      }}
                    />

                    {/* Icon */}
                    {social.icon?.url && (
                      <m.div
                        variants={vFadeIn}
                        className="relative flex size-20 items-center justify-center rounded-2xl"
                        style={{
                          background:
                            "linear-gradient(135deg, var(--social-card-icon-bg-from) 0%, var(--social-card-icon-bg-to) 100%)",
                          boxShadow:
                            "0 0 0 1px oklch(1 0 0 / 0.08), 0 8px 24px -8px var(--social-card-icon-shadow)",
                        }}
                      >
                        {/* Icon inner glow */}
                        <div
                          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 blur-md transition-opacity duration-500 group-hover:opacity-60"
                          style={{ background: "var(--sacred-orange)" }}
                        />
                        <PrismicNextImage
                          field={social.icon}
                          fallbackAlt=""
                          width={44}
                          height={44}
                          className="relative size-11 object-contain transition-transform duration-500 group-hover:scale-110"
                          imgixParams={{ auto: ["format", "compress"], q: 75 }}
                        />
                      </m.div>
                    )}

                    {/* Platform + handle */}
                    <div className="flex flex-col gap-1">
                      {social.platform && (
                        <p
                          className="text-lg font-bold tracking-wide"
                          style={{ color: "var(--sacred-gold)" }}
                        >
                          {social.platform}
                        </p>
                      )}
                      {social.handle && (
                        <p
                          className="text-sm font-medium"
                          style={{ color: "var(--social-card-text-handle)" }}
                        >
                          {social.handle}
                        </p>
                      )}
                    </div>

                    {/* Divider */}
                    <div
                      className="h-px w-12 self-center rounded-full"
                      style={{
                        background: "var(--social-card-divider)",
                      }}
                    />

                    {/* Description */}
                    {social.card_description && (
                      <p
                        className="flex-1 text-sm/relaxed"
                        style={{ color: "var(--social-card-text-desc)" }}
                      >
                        {social.card_description}
                      </p>
                    )}

                    {/* CTA */}
                    {href && (
                      <Button
                        variant="outlineOrange"
                        size="sm"
                        render={
                          <PrismicNextLink
                            field={social.cta}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        }
                      >
                        {ctaText}
                      </Button>
                    )}
                  </m.div>
                )
              })}
            </m.div>
          )}
        </m.div>
      </section>
    </LazyMotion>
  )
}

export default SocialMediaHighlight
